import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import axios from '../utils/axiosInstance';
import "bootstrap-icons/font/bootstrap-icons.css";
import UmutekanoIcyiregoForm from './UmutekanoIcyiregoForm';
import "../page/page-styles.css";

const API = "/umutekano";
const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

const theme = {
  bg: "#0b0f19", card: "#0f1525", text: "#d4d4d8", textBright: "#f1f5f9",
  muted: "#a1a1aa", primary: "#6366f1", primaryGlow: "rgba(99,102,241,0.3)",
  success: "#34d399", warning: "#fbbf24", danger: "#f87171", info: "#22d3ee",
  border: "rgba(99,102,241,0.12)", borderLight: "rgba(99,102,241,0.2)",
  gradient1: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
  gradient2: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  gradient3: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)",
  gradient4: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
};

const menuItems = [
  { key: "dashboard", icon: "bi-speedometer2", label: "Dashboard" },
  { key: "ibibazo", icon: "bi-exclamation-triangle", label: "Ibibazo by'Umutekano" },
  { key: "amakuru", icon: "bi-info-circle", label: "Amakuru y'Umutekano" },
  { key: "gukurikirana", icon: "bi-search", label: "Gukurikirana Ibibazo" },
  { key: "ubutabazi", icon: "bi-phone-flip", label: "Ubutabazi bwihuse" },
  { key: "raporo", icon: "bi-file-text", label: "Raporo" },
  { key: "icyirego", icon: "bi-file-earmark-text", label: "Icyirego" },
];

const UmutekanoDashboard = () => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', variant: '' });

  const showMsg = useCallback((text, variant) => {
    setMsg({ text, variant });
    setTimeout(() => setMsg({ text: '', variant: '' }), 4000);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    if (!token || role !== 'ROLE_UMUTEKANO') {
      navigate("/umutekano-login", { replace: true });
      return;
    }
    const fullname = localStorage.getItem("fullname") || "Umuyobozi";
    setUser({ fullname, role });
    setLoading(false);
  }, [navigate]);

  if (loading) return <div style={styles.loadingScreen}><div className="spinner-border" role="status" /></div>;

  return (
    <div style={styles.container}>
      <style>{`
        @media print { .no-print { display: none !important; } }
        .sig-canvas { border: 2px dashed ${theme.borderLight}; border-radius: 12px; touch-action: none; }
        .sig-canvas canvas { border-radius: 10px; }
      `}</style>

      {/* SIDEBAR */}
      <aside style={styles.sidebar} className="no-print">
        <div style={styles.sidebarBrand}>
          <i className="bi bi-shield-lock-fill" style={{ fontSize: "1.8rem", color: theme.primary }} />
          <span style={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}>Umutekano</span>
        </div>
        <nav style={styles.sidebarNav}>
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveMenu(item.key)}
              style={{
                ...styles.navItem,
                background: activeMenu === item.key ? theme.primary + "22" : "transparent",
                borderLeft: activeMenu === item.key ? `3px solid ${theme.primary}` : "3px solid transparent",
                color: activeMenu === item.key ? "#fff" : theme.muted,
              }}
            >
              <i className={`bi ${item.icon}`} style={{ fontSize: "1.1rem", width: 24 }} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={{ marginTop: "auto", padding: "1.5rem" }}>
          <button
            onClick={() => { localStorage.clear(); navigate("/") }}
            style={styles.logoutBtn}
          >
            <i className="bi bi-box-arrow-right me-2" /> Sohoka
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={styles.main}>
        {msg.text && (
          <div className={`alert alert-${msg.variant === 'danger' ? 'error' : 'success'} mb-4`}>
            <i className={`bi ${msg.variant === 'danger' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'}`} />
            {msg.text}
          </div>
        )}

        {activeMenu === "dashboard" && <DashboardHome user={user} onNavigate={setActiveMenu} showMsg={showMsg} />}
        {activeMenu === "ibibazo" && <IbibazoModule showMsg={showMsg} />}
        {activeMenu === "amakuru" && <AmakuruModule showMsg={showMsg} />}
        {activeMenu === "gukurikirana" && <GukurikiranaModule showMsg={showMsg} />}
        {activeMenu === "ubutabazi" && <UbutabaziModule showMsg={showMsg} />}
        {activeMenu === "raporo" && <RaporoModule showMsg={showMsg} />}
        {activeMenu === "icyirego" && <UmutekanoIcyiregoForm showMsg={showMsg} />}
      </main>
    </div>
  );
};

// ════════════════════════════════════════════
// DASHBOARD HOME
// ════════════════════════════════════════════
const DashboardHome = ({ user, onNavigate, showMsg }) => {
  const [stats, setStats] = useState({ ibibazo: 0, amakuru: 0, gukurikirana: 0, ubutabazi: 0, raporo: 0, ibirego: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [ib, am, gu, ub, ra, ic] = await Promise.all([
          axios.get(`${API}/ibibazo`, config),
          axios.get(`${API}/amakuru`, config),
          axios.get(`${API}/gukurikirana`, config),
          axios.get(`${API}/ubutabazi`, config),
          axios.get(`${API}/raporo`, config),
          axios.get(`${API}/icyirego`, config),
        ]);
        setStats({
          ibibazo: Array.isArray(ib.data) ? ib.data.length : 0,
          amakuru: Array.isArray(am.data) ? am.data.length : 0,
          gukurikirana: Array.isArray(gu.data) ? gu.data.length : 0,
          ubutabazi: Array.isArray(ub.data) ? ub.data.length : 0,
          raporo: Array.isArray(ra.data) ? ra.data.length : 0,
          ibirego: Array.isArray(ic.data) ? ic.data.length : 0,
        });
      } catch {}
    };
    fetchStats();
  }, []);

  const cards = [
    { key: "ibibazo", label: "Ibibazo by'Umutekano", val: stats.ibibazo, icon: "bi-exclamation-triangle", color: theme.warning, gradient: theme.gradient3 },
    { key: "amakuru", label: "Amakuru y'Umutekano", val: stats.amakuru, icon: "bi-info-circle", color: theme.info, gradient: theme.gradient4 },
    { key: "gukurikirana", label: "Ibibazo Bitarakemurwa", val: stats.gukurikirana, icon: "bi-search", color: theme.primary, gradient: theme.gradient1 },
    { key: "ubutabazi", label: "Ubutabazi bwihuse", val: stats.ubutabazi, icon: "bi-phone-flip", color: theme.danger, gradient: theme.gradient3 },
    { key: "raporo", label: "Raporo", val: stats.raporo, icon: "bi-file-text", color: theme.success, gradient: theme.gradient2 },
    { key: "icyirego", label: "Ibyirego", val: stats.ibirego, icon: "bi-file-earmark-text", color: "#a78bfa", gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)" },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Murakaza neza, {user?.fullname || "Umuyobozi"}!</h1>
          <p className="page-subtitle">Urubuga rw'Umuyobozi Ushinzwe Umutekano</p>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
        {cards.map((c) => (
          <button key={c.key} onClick={() => onNavigate(c.key)}
            style={{
              background: "linear-gradient(145deg,#151c30,#1a2138)", border: "1px solid rgba(99,102,241,0.12)",
              borderRadius: 16, padding: "1.25rem", cursor: "pointer", textAlign: "left", transition: "all 0.2s",
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 12, background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}>
              <i className={`bi ${c.icon}`} style={{ color: "#fff", fontSize: "1.2rem" }} />
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#f1f5f9", marginBottom: "0.25rem" }}>{c.val}</div>
            <div style={{ color: "#a1a1aa", fontSize: "0.82rem", fontWeight: 500 }}>{c.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════
// IBIBAZO BY'UMUTEKANO
// ════════════════════════════════════════════
const ubwokoOptions = ["Ubujura", "Intonganya", "Ihohoterwa", "Kwangiza Ibikorwa Remezo"];

const IbibazoModule = ({ showMsg }) => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ubwoko: "", amazina_uwaravuze: "", telephone: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" });

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/ibibazo`, config); setRecords(Array.isArray(r.data) ? r.data : []); } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/ibibazo/${editing}`, form, config);
        showMsg("Byavuguruwe neza!", "success");
      } else {
        await axios.post(`${API}/ibibazo`, form, config);
        showMsg("Ikibazo cyanditswe neza!", "success");
      }
      setShowForm(false); setEditing(null);
      setForm({ ubwoko: "", amazina_uwaravuze: "", telephone: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" });
      fetchData();
    } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleSend = async (id) => {
    try { await axios.post(`${API}/ibibazo/${id}/send`, {}, config); showMsg("Byoherejwe ku Mudugudu!", "success"); fetchData(); }
    catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleEdit = (item) => {
    setForm({ ubwoko: item.ubwoko, amazina_uwaravuze: item.amazina_uwaravuze, telephone: item.telephone || "", ahabereye: item.ahabereye, ibisobanuro: item.ibisobanuro, itariki: item.itariki?.split("T")[0] || "", igihe: item.igihe || "" });
    setEditing(item.id); setShowForm(true);
  };

  return (
    <SectionWrapper title="Kwakira no Kwandika Ibibazo by'Umutekano" icon="bi-exclamation-triangle" onAdd={() => { setEditing(null); setForm({ ubwoko: "", amazina_uwaravuze: "", telephone: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" }); setShowForm(true); }}>
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Ubwoko bw'Ikibazo</label>
              <select style={styles.input} value={form.ubwoko} onChange={(e) => setForm({ ...form, ubwoko: e.target.value })} required>
                <option value="">Hitamo ubwoko...</option>
                {ubwokoOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Amazina yatanze amakuru</label>
              <input style={styles.input} value={form.amazina_uwaravuze} onChange={(e) => setForm({ ...form, amazina_uwaravuze: e.target.value })} required placeholder="Amazina yuzuye" />
            </div>
            <div>
              <label style={styles.label}>Telephone</label>
              <input style={styles.input} value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="07XXXXXXXX" />
            </div>
            <div>
              <label style={styles.label}>Ahabereye</label>
              <input style={styles.input} value={form.ahabereye} onChange={(e) => setForm({ ...form, ahabereye: e.target.value })} required placeholder="Aho byabereye" />
            </div>
            <div>
              <label style={styles.label}>Itariki</label>
              <input type="date" style={styles.input} value={form.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} required />
            </div>
            <div>
              <label style={styles.label}>Igihe</label>
              <input type="time" style={styles.input} value={form.igihe} onChange={(e) => setForm({ ...form, igihe: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Ibisobanuro</label>
              <textarea style={{ ...styles.input, minHeight: 80, resize: "vertical" }} value={form.ibisobanuro} onChange={(e) => setForm({ ...form, ibisobanuro: e.target.value })} required placeholder="Ibisobanuro by'ikibazo" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" style={styles.btnPrimary}>{editing ? "Vugurura" : "Ohereza ku Mudugudu"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={styles.btnSecondary}>Hagarika</button>
          </div>
        </form>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "ubwoko", label: "Ubwoko" },
          { key: "amazina_uwaravuze", label: "Uwaravuze" },
          { key: "ahabereye", label: "Ahabereye" },
          { key: "itariki", label: "Itariki", render: (v) => v?.split("T")[0] },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
        ]}
        onEdit={handleEdit}
        onSend={handleSend}
        showSend
      />
    </SectionWrapper>
  );
};

// ════════════════════════════════════════════
// AMAKURU Y'UMUTEKANO
// ════════════════════════════════════════════
const AmakuruModule = ({ showMsg }) => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ inkomoko: "", ubutumwa: "", ahabereye: "", ibisobanuro: "", itariki: "" });

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/amakuru`, config); setRecords(Array.isArray(r.data) ? r.data : []); } catch {}
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await axios.put(`${API}/amakuru/${editing}`, form, config); showMsg("Byavuguruwe!", "success"); }
      else { await axios.post(`${API}/amakuru`, form, config); showMsg("Amakuru yanditswe!", "success"); }
      setShowForm(false); setEditing(null); setForm({ inkomoko: "", ubutumwa: "", ahabereye: "", ibisobanuro: "", itariki: "" }); fetchData();
    } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleSend = async (id) => {
    try { await axios.post(`${API}/amakuru/${id}/send`, {}, config); showMsg("Byoherejwe!", "success"); fetchData(); } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleEdit = (item) => {
    setForm({ inkomoko: item.inkomoko, ubutumwa: item.ubutumwa, ahabereye: item.ahabereye, ibisobanuro: item.ibisobanuro || "", itariki: item.itariki?.split("T")[0] || "" });
    setEditing(item.id); setShowForm(true);
  };

  return (
    <SectionWrapper title="Kwakira no Kohereza Amakuru y'Umutekano" icon="bi-info-circle" onAdd={() => { setEditing(null); setForm({ inkomoko: "", ubutumwa: "", ahabereye: "", ibisobanuro: "", itariki: "" }); setShowForm(true); }}>
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Inkomoko</label>
              <input style={styles.input} value={form.inkomoko} onChange={(e) => setForm({ ...form, inkomoko: e.target.value })} required placeholder="Umuturage / Polisi / ..." />
            </div>
            <div>
              <label style={styles.label}>Ahabereye</label>
              <input style={styles.input} value={form.ahabereye} onChange={(e) => setForm({ ...form, ahabereye: e.target.value })} required placeholder="Aho byabereye" />
            </div>
            <div>
              <label style={styles.label}>Itariki</label>
              <input type="date" style={styles.input} value={form.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} required />
            </div>
            <div>
              <label style={styles.label}>Ubutumwa</label>
              <textarea style={{ ...styles.input, minHeight: 80, resize: "vertical" }} value={form.ubutumwa} onChange={(e) => setForm({ ...form, ubutumwa: e.target.value })} required placeholder="Ubutumwa" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Ibisobanuro</label>
              <textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={form.ibisobanuro} onChange={(e) => setForm({ ...form, ibisobanuro: e.target.value })} placeholder="Ibisobanuro by'inyongera" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" style={styles.btnPrimary}>{editing ? "Vugurura" : "Ohereza ku Mudugudu"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={styles.btnSecondary}>Hagarika</button>
          </div>
        </form>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "inkomoko", label: "Inkomoko" },
          { key: "ubutumwa", label: "Ubutumwa", render: (v) => v?.length > 50 ? v.substring(0, 50) + "..." : v },
          { key: "ahabereye", label: "Ahabereye" },
          { key: "itariki", label: "Itariki", render: (v) => v?.split("T")[0] },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
        ]}
        onEdit={handleEdit}
        onSend={handleSend}
        showSend
      />
    </SectionWrapper>
  );
};

// ════════════════════════════════════════════
// GUKURIKIRANA IBIBAZO
// ════════════════════════════════════════════
const GukurikiranaModule = ({ showMsg }) => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ izina_ry_ikibazo: "", uko_gihagaze: "", ibyakozwe: "", ibyavuye: "", itariki: "" });

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/gukurikirana`, config); setRecords(Array.isArray(r.data) ? r.data : []); } catch {}
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await axios.put(`${API}/gukurikirana/${editing}`, form, config); showMsg("Byavuguruwe!", "success"); }
      else { await axios.post(`${API}/gukurikirana`, form, config); showMsg("Byanditswe!", "success"); }
      setShowForm(false); setEditing(null); setForm({ izina_ry_ikibazo: "", uko_gihagaze: "", ibyakozwe: "", ibyavuye: "", itariki: "" }); fetchData();
    } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleSend = async (id) => {
    try { await axios.post(`${API}/gukurikirana/${id}/send`, {}, config); showMsg("Byoherejwe!", "success"); fetchData(); } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleEdit = (item) => {
    setForm({ izina_ry_ikibazo: item.izina_ry_ikibazo, uko_gihagaze: item.uko_gihagaze, ibyakozwe: item.ibyakozwe || "", ibyavuye: item.ibyavuye || "", itariki: item.itariki?.split("T")[0] || "" });
    setEditing(item.id); setShowForm(true);
  };

  return (
    <SectionWrapper title="Gukurikirana Ibibazo Bitarakemurwa" icon="bi-search" onAdd={() => { setEditing(null); setForm({ izina_ry_ikibazo: "", uko_gihagaze: "", ibyakozwe: "", ibyavuye: "", itariki: "" }); setShowForm(true); }}>
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Ikibazo</label>
              <input style={styles.input} value={form.izina_ry_ikibazo} onChange={(e) => setForm({ ...form, izina_ry_ikibazo: e.target.value })} required placeholder="Izina ry'ikibazo" />
            </div>
            <div>
              <label style={styles.label}>Uko gikurikiranwa</label>
              <input style={styles.input} value={form.uko_gihagaze} onChange={(e) => setForm({ ...form, uko_gihagaze: e.target.value })} required placeholder="Uko gihagaze" />
            </div>
            <div>
              <label style={styles.label}>Itariki</label>
              <input type="date" style={styles.input} value={form.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} required />
            </div>
            <div>
              <label style={styles.label}>Ibyakozwe</label>
              <textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={form.ibyakozwe} onChange={(e) => setForm({ ...form, ibyakozwe: e.target.value })} placeholder="Ibyakozwe" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Ibyavuye mu gukurikirana</label>
              <textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={form.ibyavuye} onChange={(e) => setForm({ ...form, ibyavuye: e.target.value })} placeholder="Ibyavuye" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" style={styles.btnPrimary}>{editing ? "Vugurura" : "Ohereza ku Mudugudu"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={styles.btnSecondary}>Hagarika</button>
          </div>
        </form>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "izina_ry_ikibazo", label: "Ikibazo" },
          { key: "uko_gihagaze", label: "Uko gihagaze" },
          { key: "itariki", label: "Itariki", render: (v) => v?.split("T")[0] },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
        ]}
        onEdit={handleEdit}
        onSend={handleSend}
        showSend
      />
    </SectionWrapper>
  );
};

// ════════════════════════════════════════════
// UBUTABAZI BWIHUSE
// ════════════════════════════════════════════
const ubutabaziOptions = ["Inkongi z'umuriro", "Impanuka", "Ibindi bibazo byihutirwa"];

const UbutabaziModule = ({ showMsg }) => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ubwoko: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" });

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/ubutabazi`, config); setRecords(Array.isArray(r.data) ? r.data : []); } catch {}
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await axios.put(`${API}/ubutabazi/${editing}`, form, config); showMsg("Byavuguruwe!", "success"); }
      else { await axios.post(`${API}/ubutabazi`, form, config); showMsg("Byanditswe!", "success"); }
      setShowForm(false); setEditing(null); setForm({ ubwoko: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" }); fetchData();
    } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleSend = async (id) => {
    try { await axios.post(`${API}/ubutabazi/${id}/send`, {}, config); showMsg("Byoherejwe!", "success"); fetchData(); } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleEdit = (item) => {
    setForm({ ubwoko: item.ubwoko, ahabereye: item.ahabereye, ibisobanuro: item.ibisobanuro, itariki: item.itariki?.split("T")[0] || "", igihe: item.igihe || "" });
    setEditing(item.id); setShowForm(true);
  };

  return (
    <SectionWrapper title="Ibikorwa by'Ubutabazi bwihuse" icon="bi-phone-flip" onAdd={() => { setEditing(null); setForm({ ubwoko: "", ahabereye: "", ibisobanuro: "", itariki: "", igihe: "" }); setShowForm(true); }}>
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Ubwoko bw'Ibikorwa</label>
              <select style={styles.input} value={form.ubwoko} onChange={(e) => setForm({ ...form, ubwoko: e.target.value })} required>
                <option value="">Hitamo ubwoko...</option>
                {ubutabaziOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Ahabereye</label>
              <input style={styles.input} value={form.ahabereye} onChange={(e) => setForm({ ...form, ahabereye: e.target.value })} required placeholder="Aho byabereye" />
            </div>
            <div>
              <label style={styles.label}>Itariki</label>
              <input type="date" style={styles.input} value={form.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} required />
            </div>
            <div>
              <label style={styles.label}>Igihe</label>
              <input type="time" style={styles.input} value={form.igihe} onChange={(e) => setForm({ ...form, igihe: e.target.value })} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Ibisobanuro</label>
              <textarea style={{ ...styles.input, minHeight: 80, resize: "vertical" }} value={form.ibisobanuro} onChange={(e) => setForm({ ...form, ibisobanuro: e.target.value })} required placeholder="Ibisobanuro" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" style={styles.btnPrimary}>{editing ? "Vugurura" : "Ohereza ku Mudugudu"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={styles.btnSecondary}>Hagarika</button>
          </div>
        </form>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "ubwoko", label: "Ubwoko" },
          { key: "ahabereye", label: "Ahabereye" },
          { key: "itariki", label: "Itariki", render: (v) => v?.split("T")[0] },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
        ]}
        onEdit={handleEdit}
        onSend={handleSend}
        showSend
      />
    </SectionWrapper>
  );
};

// ════════════════════════════════════════════
// RAPORO
// ════════════════════════════════════════════
const raporoTypes = ["Raporo ya buri munsi", "Raporo y'icyumweru", "Raporo y'ukwezi"];

const RaporoModule = ({ showMsg }) => {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ubwoko: "", ibisobanuro: "", itariki: "" });

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/raporo`, config); setRecords(Array.isArray(r.data) ? r.data : []); } catch {}
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await axios.put(`${API}/raporo/${editing}`, form, config); showMsg("Byavuguruwe!", "success"); }
      else { await axios.post(`${API}/raporo`, form, config); showMsg("Raporo yanditswe!", "success"); }
      setShowForm(false); setEditing(null); setForm({ ubwoko: "", ibisobanuro: "", itariki: "" }); fetchData();
    } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleSend = async (id) => {
    try { await axios.post(`${API}/raporo/${id}/send`, {}, config); showMsg("Raporo yoherejwe!", "success"); fetchData(); } catch { showMsg("Habaye ikosa", "danger"); }
  };

  const handleEdit = (item) => {
    setForm({ ubwoko: item.ubwoko, ibisobanuro: item.ibisobanuro || "", itariki: item.itariki?.split("T")[0] || "" });
    setEditing(item.id); setShowForm(true);
  };

  return (
    <SectionWrapper title="Raporo" icon="bi-file-text" onAdd={() => { setEditing(null); setForm({ ubwoko: "", ibisobanuro: "", itariki: "" }); setShowForm(true); }}>
      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Ubwoko bwa Raporo</label>
              <select style={styles.input} value={form.ubwoko} onChange={(e) => setForm({ ...form, ubwoko: e.target.value })} required>
                <option value="">Hitamo...</option>
                {raporoTypes.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Itariki</label>
              <input type="date" style={styles.input} value={form.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} required />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Ibisobanuro</label>
              <textarea style={{ ...styles.input, minHeight: 100, resize: "vertical" }} value={form.ibisobanuro} onChange={(e) => setForm({ ...form, ibisobanuro: e.target.value })} placeholder="Ibisobanuro bya raporo" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button type="submit" style={styles.btnPrimary}>{editing ? "Vugurura" : "Ohereza ku Mudugudu"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={styles.btnSecondary}>Hagarika</button>
          </div>
        </form>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "ubwoko", label: "Ubwoko" },
          { key: "itariki", label: "Itariki", render: (v) => v?.split("T")[0] },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
        ]}
        onEdit={handleEdit}
        onSend={handleSend}
        showSend
      />
    </SectionWrapper>
  );
};


// ════════════════════════════════════════════
// REUSABLE COMPONENTS
// ════════════════════════════════════════════

const SectionWrapper = ({ title, icon, children, onAdd }) => (
  <div>
    <div className="page-header">
      <div>
        <h1 className="page-title"><i className={`bi ${icon} me-2`} />{title}</h1>
      </div>
      {onAdd && (
        <button onClick={onAdd} className="btn-primary-tw">
          <i className="bi bi-plus-lg" /> Ongeraho
        </button>
      )}
    </div>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const cls = {
    draft: "warning", pending: "warning", signed: "success",
    sent: "info", sent_to_umudugudu: "primary", sent_to_kagari: "success",
  };
  return (
    <span className={`badge-tw ${cls[status] || "neutral"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

const DataTable = ({ records, columns, onEdit, onSend, showSend, customActions }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;

  const filtered = useMemo(() =>
    records.filter(r => columns.some(c => String(r[c.key] || "").toLowerCase().includes(search.toLowerCase()))),
    [records, columns, search]
  );
  const totalPages = Math.ceil(filtered.length / perPage);
  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "1rem" }}>
        <div style={{ position: "relative", maxWidth: 300, flex: 1 }}>
          <i className="bi bi-search" style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "#71717a" }} />
          <input className="search-input-tw" placeholder="Shakisha..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span style={{ color: "#a1a1aa", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{filtered.length} yose</span>
      </div>
      <div className="table-wrapper-tw">
        <div className="table-scroll-tw">
          <table className="data-table">
            <thead>
              <tr>
                {columns.map(c => <th key={c.key}>{c.label}</th>)}
                <th style={{ textAlign: "right" }}>Ibikorwa</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((item, idx) => (
                <tr key={item.id || idx}>
                  {columns.map(c => (
                    <td key={c.key}>{c.render ? c.render(item[c.key]) : item[c.key] || "—"}</td>
                  ))}
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                      {onEdit && <button className="btn-action-tw edit" onClick={() => onEdit(item)} title="Hindura"><i className="bi bi-pencil" /></button>}
                      {showSend && item.status !== "sent" && item.status !== "sent_to_umudugudu" && onSend && (
                        <button className="btn-action-tw view" onClick={() => onSend(item.id)} title="Ohereza"><i className="bi bi-send" /></button>
                      )}
                      {customActions && customActions(item)}
                    </div>
                  </td>
                </tr>
              ))}
              {paged.length === 0 && (
                <tr><td colSpan={columns.length + 1} style={{ textAlign: "center", color: "#71717a", padding: "2rem" }}>Nta makuru aboneka</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "0.25rem", marginTop: "1rem" }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} className={p === page ? "btn-primary-tw" : "btn-outline-tw"}
              style={{ minWidth: 36, height: 36, padding: 0, fontSize: "0.8rem" }}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════
const styles = {
  container: {
    display: "flex", minHeight: "100vh", background: "#0b0f19", color: "#d4d4d8",
    fontFamily: "'Nunito Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
  sidebar: {
    width: 260, background: "#0f1525", borderRight: "1px solid rgba(99,102,241,0.12)",
    display: "flex", flexDirection: "column", flexShrink: 0,
  },
  sidebarBrand: {
    padding: "1.5rem", display: "flex", alignItems: "center", gap: "0.75rem",
    borderBottom: "1px solid rgba(99,102,241,0.12)",
  },
  sidebarNav: { padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.15rem", flex: 1 },
  navItem: {
    display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.7rem 0.9rem",
    borderRadius: 10, fontSize: "0.88rem", fontWeight: 500, cursor: "pointer",
    transition: "all 0.2s", border: "none", background: "transparent", width: "100%", textAlign: "left",
  },
  logoutBtn: {
    width: "100%", padding: "0.65rem 1rem", borderRadius: 10,
    border: "1px solid rgba(99,102,241,0.2)", background: "transparent",
    color: "#a1a1aa", cursor: "pointer", fontSize: "0.85rem",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  main: {
    flex: 1, padding: "2rem", overflow: "auto", maxWidth: "100%",
  },
  loadingScreen: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: "#0b0f19",
  },
  formCard: {
    background: "linear-gradient(145deg,#0f1525,#141a2e)", border: "1px solid rgba(99,102,241,0.12)",
    borderRadius: 16, padding: "1.5rem", marginBottom: "1.5rem",
  },
  formGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "1rem",
  },
  label: {
    display: "block", color: "#a1a1aa", fontSize: "0.75rem", fontWeight: 600,
    marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "0.65rem 0.85rem", borderRadius: 10,
    border: "1px solid rgba(99,102,241,0.2)", background: "rgba(30,41,59,0.85)",
    color: "#f1f5f9", fontSize: "0.875rem", outline: "none", boxSizing: "border-box",
  },
  btnPrimary: {
    padding: "0.6rem 1.2rem", borderRadius: 10, border: "none",
    background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff",
    fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
    display: "flex", alignItems: "center", gap: "0.35rem", fontFamily: "inherit",
  },
  btnSecondary: {
    padding: "0.6rem 1.2rem", borderRadius: 10, border: "1px solid rgba(99,102,241,0.2)",
    background: "transparent", color: "#a1a1aa", fontWeight: 600, fontSize: "0.85rem",
    cursor: "pointer", fontFamily: "inherit",
  },
  smallBtn: {
    width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(99,102,241,0.2)",
    background: "transparent", color: "#a1a1aa", cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem",
  },
  docField: {
    marginBottom: "1rem", padding: "0.75rem", background: "rgba(30,41,59,0.5)",
    borderRadius: 10, border: "1px solid rgba(99,102,241,0.12)",
  },
  docLabel: {
    fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase",
    color: "#a1a1aa", letterSpacing: "0.05em", marginBottom: "0.25rem",
  },
  docValue: {
    fontSize: "1rem", color: "#f1f5f9", fontWeight: 500,
  },
};

export default UmutekanoDashboard;
